/**
 * Contents of the "Add CD Template" modal in `RepoConfigEditor`.
 *
 * Two configuration modes coexist and are user-switchable at any time:
 *   - `infra`: pick a FINISHED infrastructure provisioned via the "Creación de
 *     IaC MVP" golden path (`InfraCdSelector`) and let the backend build the
 *     CD configuration from its outputs (`PUT .../templates/from-infra`).
 *   - `manual`: pick a CD template by hand (`TemplateSelectorFieldCD`) and
 *     fill its full editor form (`DynamicFormGeneratorFieldCD`), exactly as
 *     before the infra-driven flow existed.
 *
 * Both were, for a time, mutually exclusive: users in the `iac-mvp-access`
 * OpenFGA group only saw `infra` with no way out, everyone else only saw
 * `manual`. That restriction has been reverted — every user sees `infra`
 * first (when the repo's APM can be resolved) with an always-available link
 * to switch to `manual`, and vice versa. See this repo's
 * `helm-deploy-gke-plataforma` CHANGELOG.md for the history of that
 * decision.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@material-ui/core';
import { Theme as RJSFTheme } from '@rjsf/material-ui';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { TemplateSelectorFieldCD } from '../../../../components/scaffolder/TemplateSelectorField/TemplateSelectorFieldCD';
import { DynamicFormGeneratorFieldCD } from '../../../scaffolder/DynamicFormGeneratorField/DynamicFormGeneratorFieldCD';
import { InfraCdSelector, InfraCdSelection } from './InfraCdSelector';
import { QuickCdConfigForm } from './QuickCdConfigForm';
import {
  InfraCdConfirmation,
  InfraCdError,
  InfraCdErrorPanel,
  InfraCdPreview,
} from './InfraCdConfirmation';

const rjsfRegistry = {
  fields: {},
  templates: { ...(RJSFTheme.templates as any) },
  widgets: {},
  formContext: {},
  rootSchema: {},
  schemaUtils: (RJSFTheme as any).schemaUtils || {},
  translateString: (stringKey: string) => stringKey,
};

type Mode = 'resolving-apm' | 'infra' | 'manual';

export interface CdConfigurationPanelProps {
  repositoryName?: string;
  entity: Entity;
  /** Environment the "+" that opened the modal was clicked on (des/int/sta/pre/pro), or null for the generic add button. */
  initialTargetEnv: string | null;
  /** Fallback environment used by the manual flow when no specific env was targeted. */
  environment: string;
  /** Called when selecting an infra resolves/locks a target environment (mirrors the infra's mapped environment). */
  onEnvironmentResolved: (env: string) => void;
  onAlert: (type: 'info' | 'success' | 'error', message: string) => void;
  /** Common post-save steps (refresh entity, success alert, close modal, reload environments/page). */
  onSaved: () => Promise<void> | void;
}

/**
 * Contents of the "Add CD Template" dialog: owns both configuration modes and
 * the switch between them. `RepoConfigEditor` only supplies identifying props
 * and callbacks for cross-cutting concerns (alerts, saving side effects); it
 * does not know which mode is active.
 */
export const CdConfigurationPanel = (props: CdConfigurationPanelProps) => {
  const { repositoryName, entity, initialTargetEnv, environment, onEnvironmentResolved, onAlert, onSaved } = props;
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [mode, setMode] = useState<Mode>('resolving-apm');
  const [apm, setApm] = useState<string | null>(null);

  // --- infra mode state ---------------------------------------------------
  const [selectedInfraWorkflowId, setSelectedInfraWorkflowId] = useState<string | null>(null);
  const [quickStep, setQuickStep] = useState<'inactive' | 'active' | 'headless' | 'error'>('inactive');
  const [quickValues, setQuickValues] = useState<Record<string, any> | null>(null);
  const [infraPreview, setInfraPreview] = useState<InfraCdPreview | null>(null);
  const [infraError, setInfraError] = useState<InfraCdError | null>(null);
  const [isHeadlessSubmitting, setIsHeadlessSubmitting] = useState(false);
  // Unfiltered template catalog ({name, current_version, ...}[]) plus the
  // default branch, cached for the lifetime of the panel. Needed to resolve
  // the version of the GP-declared CD template, which is hidden from the
  // manual selector by type.
  const templatesCatalogRef = useRef<{ list: any[]; defaultBranch: string } | null>(null);

  // Shared by both modes: the template/version/branch (+ manual form payload)
  // being configured. Cleared whenever the mode switches so neither flow
  // leaks state into the other.
  const [templateSelectorValue, setTemplateSelectorValue] = useState<any>(null);
  const [cdFormPayload, setCdFormPayload] = useState<any>(null);

  // Confirmation dialog shown when leaving `manual` for `infra` with unsaved
  // manual input, so the user does not lose it by accident.
  const [manualSwitchConfirmOpen, setManualSwitchConfirmOpen] = useState(false);

  // Whether `manual` was entered automatically because there was nothing to
  // pick from `infra` (no resolvable APM, or an APM with zero provisioned
  // infra), as opposed to the user choosing it via the fallback link. Only
  // used to tailor the explanatory message shown in `manual` mode; it does
  // NOT gate the "volver a infra" link, so the user can still go back and
  // retry (e.g. after provisioning an infra).
  const [autoManualReason, setAutoManualReason] = useState<'no-apm' | 'no-infra' | null>(null);
  // Guards against InfraCdSelector's availability check re-triggering the
  // auto-switch every time the user comes back to `infra` mode and it is
  // still empty: only the FIRST empty determination auto-switches.
  const autoSwitchedRef = useRef(false);

  // Resolve the APM from the component's linked Application (spec.system →
  // Application.spec.number). Best-effort: if it cannot be resolved, `infra`
  // mode still activates but with no infra to pick (InfraCdSelector needs an
  // apm; without it we just show the manual-fallback link).
  useEffect(() => {
    let cancelled = false;
    const resolveApm = async () => {
      const system = entity?.spec?.system as string | undefined;
      let resolved: string | null = null;
      if (system) {
        try {
          const appRef = system.includes(':') ? system : `application:default/${system}`;
          const app = await catalogApi.getEntityByRef(appRef);
          const number = (app?.spec as any)?.number as string | undefined;
          resolved = number ? String(number).toUpperCase() : null;
        } catch {
          resolved = null;
        }
      }
      if (!cancelled) {
        setApm(resolved);
        if (resolved) {
          setMode('infra');
        } else {
          // No APM means there is nothing InfraCdSelector could ever list for
          // this repo: go straight to manual instead of making the user
          // notice that and click the fallback link themselves.
          autoSwitchedRef.current = true;
          setAutoManualReason('no-apm');
          setMode('manual');
        }
      }
    };
    resolveApm();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplatesCatalog = async () => {
    if (templatesCatalogRef.current) return templatesCatalogRef.current;
    const token = (await identityApi.getCredentials()).token;
    const response = await fetch(`${backendUrl}/api/devops-api-proxy/templates?templateBranch=`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    templatesCatalogRef.current = {
      list: Array.isArray(data.apiPayload) ? data.apiPayload : [],
      defaultBranch: data.defaultBranch || '',
    };
    return templatesCatalogRef.current;
  };

  // When a FINISHED infra is selected: lock the target environment to the
  // infra's (mapped) environment, resolve the version/branch of the CD
  // template declared by its golden path (the template is hidden from the
  // manual selector by type, so the lookup uses the unfiltered catalog), and
  // start the quick-config step. A resolution failure surfaces as a blocking
  // error (quickStep 'error'), from which the user can retry or switch to
  // `manual`.
  const handleSelectInfra = async (sel: InfraCdSelection) => {
    setSelectedInfraWorkflowId(sel.workflowId);
    if (sel.environment) {
      onEnvironmentResolved(sel.environment);
    }
    setQuickValues(null);
    setInfraPreview(null);
    setInfraError(null);
    setQuickStep('inactive');

    if (!sel.cdTemplate) {
      setInfraError({
        message: 'La infraestructura seleccionada no declara una plantilla de CD en su golden path. No es posible configurar el CD automáticamente para esta infraestructura.',
      });
      setQuickStep('error');
      return;
    }

    // sel.cdTemplate is the raw acciona.io/cd-template annotation value,
    // "<name>:<version>" — the backend's from-infra endpoint splits it the
    // same way and uses that exact version (not the catalog's
    // current_version, which may have moved on since the golden path was
    // annotated), so the frontend must resolve it identically.
    const [templateName, templateVersion] = sel.cdTemplate.split(':');
    if (!templateName || !templateVersion) {
      setInfraError({
        message: `La plantilla de CD declarada por el golden path ("${sel.cdTemplate}") no tiene el formato "<nombre>:<versión>" esperado.`,
      });
      setQuickStep('error');
      return;
    }

    try {
      const { list, defaultBranch } = await fetchTemplatesCatalog();
      const entry = list.find((t: any) => t?.name === templateName);
      if (!entry) {
        setInfraError({
          message: `La plantilla de CD "${templateName}" declarada por el golden path no existe en el catálogo de plantillas.`,
        });
        setQuickStep('error');
        return;
      }
      setTemplateSelectorValue({
        template: templateName,
        version: templateVersion,
        branch: defaultBranch,
        deployEnvironment: sel.environment,
      });
      setQuickStep('active');
    } catch (err) {
      setInfraError({
        message: `Error resolviendo la plantilla de CD: ${err instanceof Error ? err.message : err}`,
      });
      setQuickStep('error');
    }
  };

  /**
   * Calls PUT /repositories/:repo/templates/from-infra: the backend fetches
   * the workflow outputs service-to-service, resolves the template's
   * x-infra-output mapping and (unless dryRun) persists cd_values plus the
   * generated infra_values.yaml. A 422 means some output did not resolve.
   */
  const callFromInfra = async (opts: { dryRun: boolean; values: Record<string, any> | null }) => {
    if (!repositoryName || !selectedInfraWorkflowId) {
      throw new Error('Repository or infrastructure selection missing');
    }
    const env = initialTargetEnv ?? environment;
    const token = (await identityApi.getCredentials()).token;
    const url = `${backendUrl}/api/devops-api-proxy/repositories/${repositoryName}/templates/from-infra?environment=${encodeURIComponent(env)}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        workflowId: selectedInfraWorkflowId,
        quickValues: opts.values ?? undefined,
        entity,
        dryRun: opts.dryRun,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      // `details` is kept structured (not folded into the message) so the
      // error panel can hide it behind the platform-only accordion.
      const error: any = new Error(data.error || `HTTP ${response.status}`);
      error.status = response.status;
      if (Array.isArray(data.details) && data.details.length > 0) {
        error.details = data.details;
      }
      throw error;
    }
    return data;
  };

  // Single entry point for both the quick-config "Continuar" and "Omitir"
  // actions: runs the server-side dry run and either shows the headless
  // preview or a blocking error (from which the user can retry or fall back
  // to `manual`).
  const attemptInfraResolution = async (values: Record<string, any>) => {
    setQuickValues(values);
    try {
      const preview = await callFromInfra({ dryRun: true, values });
      setInfraPreview(preview);
      setInfraError(null);
      setQuickStep('headless');
    } catch (err) {
      setInfraPreview(null);
      setInfraError({
        message: err instanceof Error ? err.message : String(err),
        details: (err as any)?.details,
      });
      setQuickStep('error');
    }
  };

  // Save from the headless quick-config flow (the full form is not mounted,
  // so its submit button does not exist). Persists through the from-infra
  // endpoint and repeats the same post-save steps as the manual flow.
  const handleHeadlessSave = async () => {
    if (isHeadlessSubmitting) return;
    setIsHeadlessSubmitting(true);
    onAlert('info', 'Saving CD template configuration...');
    try {
      await callFromInfra({ dryRun: false, values: quickValues });
      await onSaved();
    } catch (err) {
      onAlert('error', `Error: ${err}`);
    } finally {
      setIsHeadlessSubmitting(false);
    }
  };

  const handleSubmitCdTemplate = async () => {
    if (!repositoryName || !cdFormPayload) {
      onAlert('error', 'Repository or form data missing');
      return;
    }
    onAlert('info', 'Saving CD template configuration...');
    try {
      const payload = { cdFormPayload, entity };
      const token = (await identityApi.getCredentials()).token;
      const baseUrl = `${backendUrl}/api/devops-api-proxy/repositories/${repositoryName}/templates`;
      // Si viene de una tarjeta → template por entorno
      const url = initialTargetEnv ? `${baseUrl}?environment=${initialTargetEnv}` : baseUrl;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save template config');
      }
      await onSaved();
    } catch (err) {
      onAlert('error', `Error: ${err}`);
    }
  };

  // --- mode switching -------------------------------------------------------

  const hasManualDraft = () =>
    Boolean(templateSelectorValue?.template) ||
    (cdFormPayload && Object.keys(cdFormPayload).length > 0);

  const goManual = (reason: 'no-apm' | 'no-infra' | null = null) => {
    setSelectedInfraWorkflowId(null);
    setQuickStep('inactive');
    setQuickValues(null);
    setInfraPreview(null);
    setInfraError(null);
    setTemplateSelectorValue(null);
    setCdFormPayload(null);
    setAutoManualReason(reason);
    setMode('manual');
  };

  // Called by InfraCdSelector once its list has loaded and settled. Only the
  // first "there is nothing to select" result auto-switches to manual, so a
  // user who deliberately goes back to `infra` (e.g. to check whether an
  // infra just finished provisioning) sees the selector's own empty state
  // instead of being bounced straight back out.
  const handleInfraAvailabilityChange = (hasInfra: boolean) => {
    if (hasInfra || autoSwitchedRef.current) return;
    autoSwitchedRef.current = true;
    goManual('no-infra');
  };

  const confirmGoInfra = () => {
    setManualSwitchConfirmOpen(false);
    setTemplateSelectorValue(null);
    setCdFormPayload(null);
    setMode('infra');
  };

  const goInfra = () => {
    if (hasManualDraft()) {
      setManualSwitchConfirmOpen(true);
      return;
    }
    confirmGoInfra();
  };

  if (mode === 'resolving-apm') {
    return (
      <Box display="flex" alignItems="center" gridGap={8} style={{ padding: 8 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Comprobando configuración disponible…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {mode === 'infra' && (
        <Box style={{ marginBottom: 16 }}>
          {apm && (
            <InfraCdSelector
              apm={apm}
              targetCdEnv={initialTargetEnv}
              selectedWorkflowId={selectedInfraWorkflowId}
              onSelect={handleSelectInfra}
              onAvailabilityChange={handleInfraAvailabilityChange}
            />
          )}
          {quickStep === 'active' && templateSelectorValue?.template && templateSelectorValue?.version && (
            <QuickCdConfigForm
              template={templateSelectorValue.template}
              version={templateSelectorValue.version}
              branch={templateSelectorValue.branch || ''}
              onConfirm={values => attemptInfraResolution(values)}
              onSkip={() => attemptInfraResolution({})}
            />
          )}
          {quickStep === 'headless' && infraPreview && selectedInfraWorkflowId && (
            <InfraCdConfirmation
              preview={infraPreview}
              workflowId={selectedInfraWorkflowId}
              saving={isHeadlessSubmitting}
              onSave={handleHeadlessSave}
            />
          )}
          {quickStep === 'error' && infraError && (
            <InfraCdErrorPanel
              error={infraError}
              workflowId={selectedInfraWorkflowId ?? '-'}
              onRetry={quickValues !== null ? () => attemptInfraResolution(quickValues) : undefined}
              onManualFallback={() => goManual()}
            />
          )}
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              ¿No tienes infraestructura o prefieres configurarlo tú mismo?{' '}
              <Link component="button" variant="body2" onClick={() => goManual()}>
                Configura la plantilla manualmente
              </Link>
            </Typography>
          </Box>
        </Box>
      )}

      {mode === 'manual' && (
        <Box>
          <Typography variant="subtitle2">Configuración manual de plantilla</Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
            {autoManualReason === 'no-apm' &&
              'No se ha podido determinar la aplicación (APM) de este repositorio, así que no hay infraestructuras que listar: configura la plantilla manualmente.'}
            {autoManualReason === 'no-infra' &&
              'Este APM no tiene infraestructuras aprovisionadas por el golden path "Creación de IaC MVP" (o ninguna coincide con este entorno): configura la plantilla manualmente, o vuelve a intentarlo por infraestructura una vez la hayas provisionado.'}
            {!autoManualReason &&
              'Selecciona y configura una plantilla de CD sin partir de una infraestructura ya aprovisionada.'}
          </Typography>
          <TemplateSelectorFieldCD
            formData={templateSelectorValue}
            onChange={(value: any) => {
              setCdFormPayload(null);
              setTemplateSelectorValue(value);
            }}
            schema={{}}
            uiSchema={{}}
            required
            idSchema={{ $id: 'cdDynamicForm' }}
            name="cdTemplateSelector"
            rawErrors={[]}
            template_type="cd"
            repository={repositoryName}
            entity={entity}
            environment={initialTargetEnv ?? undefined}
            onBlur={() => {}}
            onFocus={() => {}}
            disabled={false}
            readonly={false}
            registry={rjsfRegistry}
          />
          {templateSelectorValue?.template && templateSelectorValue?.version && (
            <div style={{ marginTop: 24 }}>
              <DynamicFormGeneratorFieldCD
                onSubmit={handleSubmitCdTemplate}
                TemplateSelectorFieldProps={{}}
                formData={cdFormPayload}
                onChange={(data: any) => setCdFormPayload(data)}
                schema={{}}
                uiSchema={{}}
                required={false}
                idSchema={{ $id: 'cdDynamicForm' }}
                name="cdDynamicForm"
                rawErrors={[]}
                formContext={{
                  formData: {
                    templateConfig: {
                      template: templateSelectorValue.template,
                      version: templateSelectorValue.version,
                      branch: templateSelectorValue.branch || '',
                      artifactoryOutputs: templateSelectorValue.artifactoryOutputs,
                      skipInfraDropdown: templateSelectorValue.skipInfraDropdown,
                    },
                    entity,
                  },
                }}
                onBlur={() => {}}
                onFocus={() => {}}
                disabled={false}
                readonly={false}
                registry={rjsfRegistry}
              />
            </div>
          )}
          {apm && (
            <Box mt={2}>
              <Link component="button" variant="body2" onClick={goInfra}>
                ← Volver a selección por infraestructura
              </Link>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={manualSwitchConfirmOpen} onClose={() => setManualSwitchConfirmOpen(false)}>
        <DialogTitle>¿Descartar configuración manual?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Vas a descartar la configuración manual introducida y volver a la selección por infraestructura.
            ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualSwitchConfirmOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={confirmGoInfra}>
            Descartar y volver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
